import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ListTree, Plus, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Alternador, AreaSensivel, Entrada, Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { textoLivre } from '../../nucleo/formato';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { ErroRequisicao, api, del, get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import type { Fila, Menu, PalavraChave } from './tipos';

type CanalMenu = Menu['canal'];
const CANAIS_MENU: CanalMenu[] = ['whatsapp', 'instagram'];
const CLASSE_AREA = 'w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco';

function useFilasAtivas() {
  return useQuery({ queryKey: ['conversas', 'filas', 'ativas'], queryFn: () => getTodos<Fila>('/conversas/filas', { ativa: true }) });
}

/**
 * Trilha 5b (Digisac): formulário do menu de entrada de um canal. Opções numeradas pela posição (1 a 9), cada uma com o
 * nome e a fila; gaveta, rótulos visíveis e erro por campo, como o das filas.
 */
function FormMenu({ canal, menu, aoConcluir, aoReabrir }: { canal: CanalMenu; menu: Menu | null; aoConcluir: () => void; aoReabrir: () => void }) {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const filas = useFilasAtivas();
  // Review r55 robustez (B1): a versão que a pessoa abriu fica fixa; o dado novo que chega (outra pessoa gravou) não remonta
  // o formulário nem troca a versão enviada, e o rascunho fica até ela escolher reabrir com a versão atual.
  const [base] = useState(menu);
  const [conflito, setConflito] = useState(false);
  const [ativo, setAtivo] = useState(menu?.ativo ?? true);
  const [mensagem, setMensagem] = useState(menu?.mensagem ?? '');
  const [opcoes, setOpcoes] = useState<{ rotulo: string; fila_id: string }[]>(menu?.opcoes.map((o) => ({ rotulo: o.rotulo, fila_id: o.fila_id })) ?? [{ rotulo: '', fila_id: '' }]);
  const [invalida, setInvalida] = useState(menu?.mensagem_invalida ?? '');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  useValidacaoLocal(erros, erroGeral);

  const salvar = useMutation({
    mutationFn: () =>
      api('PUT', `/conversas/menus/${canal}`, {
        ativo,
        mensagem: mensagem.trim(),
        opcoes: opcoes.map((o, i) => ({ tecla: String(i + 1), rotulo: o.rotulo.trim(), fila_id: o.fila_id })),
        mensagem_invalida: invalida.trim() || null,
        // Review r54 robustez (B2): o menu que a tela carregou (null = criar). Se outro foi gravado nesse meio-tempo, o
        // servidor recusa (409 `menu.conflito`) em vez de substituir o que a pessoa nunca viu. Review r55 (B1): com a versão.
        menu_id: base?.id ?? null,
        ...(base ? { menu_atualizado_em: base.updated_at } : {}),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['conversas', 'menus'] });
      toast.sucesso(t('t5b.automacoes.salvo'));
      aoConcluir();
    },
    onError: (e) => {
      // Review r55 visual (B1): no conflito a lista é relida e a tela oferece reabrir com a versão nova.
      if (e instanceof ErroRequisicao && e.corpo.code === 'menu.conflito') {
        void qc.invalidateQueries({ queryKey: ['conversas', 'menus'] });
        setErros({});
        setErroGeral(null);
        setConflito(true);
        return;
      }
      if (e instanceof ErroRequisicao) {
        const porCampo = e.porCampo();
        setErros(porCampo);
        setErroGeral(Object.keys(porCampo).length ? null : mensagemDeErro(e));
      } else setErroGeral(mensagemDeErro(e));
    },
  });

  const opcoesFila = [{ valor: '', rotulo: tn('comum.selecione') }, ...(filas.data?.itens ?? []).map((f) => ({ valor: f.id, rotulo: f.nome }))];
  return (
    <form
      id="form-menu"
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setErroGeral(null);
        const locais: Record<string, string> = {};
        if (!mensagem.trim()) locais.mensagem = tn('erros.obrigatorio');
        if (!opcoes.length || opcoes.some((o) => !o.rotulo.trim() || !o.fila_id)) locais.opcoes = tn('erros.obrigatorio');
        setErros(locais);
        if (Object.keys(locais).length) return;
        salvar.mutate();
      }}
    >
      {erroGeral && (
        <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
          {erroGeral}
        </p>
      )}
      {conflito && (
        <div role="alert" className="flex min-w-0 flex-col gap-2 rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
          <p className="min-w-0 break-words">{t('t5b.automacoes.conflito')}</p>
          <Botao variante="secundario" tamanho="sm" className="self-start" onClick={aoReabrir}>
            {t('t5b.automacoes.reabrirAtual')}
          </Botao>
        </div>
      )}
      {!filas.isPending && !filas.data?.itens.length && <p className="text-sm text-tinta-3">{t('t5b.automacoes.semFilas')}</p>}
      <Rotulo id="menu-mensagem" rotulo={t('t5b.automacoes.mensagem')} obrigatorio erro={erros.mensagem}>
        <AreaSensivel id="menu-mensagem" rotulo={t('t5b.automacoes.mensagem')} rows={3} maxLength={1000} value={mensagem} onChange={(e) => setMensagem(e.target.value)} className={CLASSE_AREA} />
      </Rotulo>
      <fieldset className="flex min-w-0 flex-col gap-2">
        <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('t5b.automacoes.opcoes')}</legend>
        {erros.opcoes && <p className="text-xs text-perigo">{erros.opcoes}</p>}
        <ol className="flex min-w-0 flex-col gap-2">
          {opcoes.map((o, i) => (
            <li key={i} className="flex min-w-0 flex-wrap items-end gap-2 rounded-lg border border-borda bg-sup-2 px-3 py-2">
              <span className="numero w-6 shrink-0 pb-2 text-sm font-semibold text-tinta-2" aria-hidden>
                {i + 1}
              </span>
              <label className="flex min-w-0 flex-1 basis-40 flex-col gap-1 text-xs text-tinta-3">
                {t('t5b.automacoes.rotulo', { n: i + 1 })}
                <Entrada value={o.rotulo} maxLength={60} onChange={(e) => setOpcoes((l) => l.map((x, j) => (j === i ? { ...x, rotulo: e.target.value } : x)))} />
              </label>
              <label className="flex min-w-0 flex-1 basis-40 flex-col gap-1 text-xs text-tinta-3">
                {t('t5b.automacoes.filaOpcao', { n: i + 1 })}
                <Selecao opcoes={opcoesFila} value={o.fila_id} onChange={(e) => setOpcoes((l) => l.map((x, j) => (j === i ? { ...x, fila_id: e.target.value } : x)))} />
              </label>
              <Botao variante="fantasma" tamanho="sm" disabled={opcoes.length === 1} aria-label={t('t5b.automacoes.removerOpcao', { n: i + 1 })} icone={<X className="size-4" />} onClick={() => setOpcoes((l) => l.filter((_, j) => j !== i))} />
            </li>
          ))}
        </ol>
        {opcoes.length < 9 && (
          <Botao variante="fantasma" tamanho="sm" className="w-fit" icone={<Plus className="size-4" />} onClick={() => setOpcoes((l) => [...l, { rotulo: '', fila_id: '' }])}>
            {t('t5b.automacoes.adicionarOpcao')}
          </Botao>
        )}
      </fieldset>
      <Rotulo id="menu-invalida" rotulo={t('t5b.automacoes.mensagemInvalida')} ajuda={t('t5b.automacoes.ajudaInvalida')} erro={erros.mensagem_invalida}>
        <AreaSensivel id="menu-invalida" rotulo={t('t5b.automacoes.mensagemInvalida')} rows={2} maxLength={500} value={invalida} onChange={(e) => setInvalida(e.target.value)} className={CLASSE_AREA} />
      </Rotulo>
      <div className="flex items-center justify-between gap-3 rounded-lg border border-borda bg-sup-2 px-3 py-2">
        <label htmlFor="menu-ativo" className="text-sm font-medium text-tinta-2">
          {t('t5b.automacoes.ativo')}
        </label>
        <Alternador id="menu-ativo" rotulo={t('t5b.automacoes.ativo')} marcado={ativo} aoMudar={setAtivo} />
      </div>
    </form>
  );
}

/** Cartão do menu de um canal: estado, opções e o botão de montar ou editar. */
function MenuDoCanal({ canal, menu, podeEditar, aoAbrir }: { canal: CanalMenu; menu: Menu | null; podeEditar: boolean; aoAbrir: () => void }) {
  const { t } = useTranslation('conversas');
  return (
    <Cartao
      titulo={t(`w9.canais.${canal}`)}
      acao={
        podeEditar ? (
          <Botao tamanho="sm" icone={<ListTree className="size-4" />} onClick={aoAbrir}>
            {menu ? t('t5b.automacoes.editarMenu') : t('t5b.automacoes.criarMenu')}
          </Botao>
        ) : undefined
      }
    >
      {menu ? (
        <div className="flex min-w-0 flex-col gap-2 text-sm">
          <span>
            <Selo cor={menu.ativo ? 'verde' : 'neutro'}>{menu.ativo ? t('t5b.automacoes.ativo') : t('t5b.automacoes.inativo')}</Selo>
          </span>
          <p className="min-w-0 break-words whitespace-pre-wrap text-tinta-2">{textoLivre(menu.mensagem)}</p>
          <ol className="flex min-w-0 flex-col gap-0.5 text-tinta-2">
            {menu.opcoes.map((o) => (
              <li key={o.tecla} className="min-w-0 break-words">
                <span className="numero font-semibold">{o.tecla})</span> {o.rotulo}
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <p className="text-sm text-tinta-3">{t('t5b.automacoes.semMenu')}</p>
      )}
    </Cartao>
  );
}

export function Automacoes() {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  // Review r46 visual M1: menu e palavras-chave valem para o workspace (automacao_exige_todas); o restrito só lê, com o aviso.
  // Val5 (29/10): menus e palavras-chave são configuração do atendimento (`conversas.configurar`).
  const podeEditar = podeNoWorkspace(s, 'conversas.configurar');
  const menus = useQuery({ queryKey: ['conversas', 'menus'], queryFn: () => get<{ itens: Menu[] }>('/conversas/menus') });
  const filas = useFilasAtivas();
  const [canalAberto, setCanalAberto] = useState<CanalMenu | null>(null);
  const [geracao, setGeracao] = useState(0);
  const [removerMenu, setRemoverMenu] = useState(false);
  const [regra, setRegra] = useState<PalavraChave | 'nova' | null>(null);
  const [removerRegra, setRemoverRegra] = useState(false);
  const menuDe = (c: CanalMenu) => menus.data?.itens.find((m) => m.canal === c) ?? null;
  const menuAberto = canalAberto ? menuDe(canalAberto) : null;
  const regraAtual = regra && regra !== 'nova' ? regra : null;

  const excluirMenu = useMutation({
    mutationFn: (c: CanalMenu) => del(`/conversas/menus/${c}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['conversas', 'menus'] });
      toast.sucesso(t('t5b.automacoes.removido'));
      setRemoverMenu(false);
      setCanalAberto(null);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const atualizarRegras = () => qc.invalidateQueries({ queryKey: ['/conversas/palavras-chave'] });
  const excluirRegra = useMutation({
    mutationFn: (id: string) => del(`/conversas/palavras-chave/${id}`),
    onSuccess: async () => {
      await atualizarRegras();
      toast.sucesso(tn('comum.removido'));
      setRemoverRegra(false);
      setRegra(null);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const campos: DefCampo[] = [
    { nome: 'palavras', rotulo: t('t5b.automacoes.palavras'), tipo: 'tags', obrigatorio: true, ajuda: t('t5b.automacoes.ajudaPalavras'), desabilitado: !podeEditar },
    { nome: 'resposta', rotulo: t('t5b.automacoes.resposta'), tipo: 'area', obrigatorio: true, desabilitado: !podeEditar },
    {
      nome: 'canal',
      rotulo: t('t5b.automacoes.canal'),
      tipo: 'selecao',
      largura: 'meia',
      desabilitado: !podeEditar,
      opcoes: [{ valor: '', rotulo: t('t5b.automacoes.todosCanais') }, ...CANAIS_MENU.map((c) => ({ valor: c, rotulo: t(`w9.canais.${c}`) }))],
    },
    {
      nome: 'fila_id',
      rotulo: t('t5b.automacoes.fila'),
      tipo: 'selecao',
      largura: 'meia',
      desabilitado: !podeEditar,
      opcoes: [{ valor: '', rotulo: t('t5b.automacoes.qualquerFila') }, ...(filas.data?.itens ?? []).map((f) => ({ valor: f.id, rotulo: f.nome }))],
    },
    { nome: 'ativa', rotulo: t('t5b.automacoes.ativa'), tipo: 'booleano', desabilitado: !podeEditar },
  ];
  const colunas: Coluna<PalavraChave>[] = [
    { chave: 'palavras', rotulo: t('t5b.automacoes.palavras'), principal: true, render: (r) => <TextoCortado className="min-w-0 font-medium">{r.palavras.join(', ')}</TextoCortado> },
    { chave: 'resposta', rotulo: t('t5b.automacoes.resposta'), render: (r) => <span className="line-clamp-2 whitespace-pre-wrap" title={r.resposta}>{r.resposta}</span> },
    { chave: 'canal', rotulo: t('t5b.automacoes.canal'), render: (r) => (r.canal ? t(`w9.canais.${r.canal}`) : t('t5b.automacoes.todosCanais')) },
    { chave: 'fila_nome', rotulo: t('t5b.automacoes.fila'), render: (r) => r.fila_nome ?? t('t5b.automacoes.qualquerFila') },
    { chave: 'ativa', rotulo: t('t5b.automacoes.ativa'), render: (r) => <Selo cor={r.ativa ? 'verde' : 'neutro'}>{r.ativa ? t('t5b.automacoes.ativa') : t('t5b.automacoes.inativa')}</Selo> },
  ];

  return (
    <>
      {podeEditar && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setRegra('nova')}>
            {t('t5b.automacoes.nova')}
          </Botao>
        </AcaoPrimaria>
      )}
      <div className="flex min-w-0 flex-col gap-4">
        <section className="flex min-w-0 flex-col gap-2">
          <h2 className="text-sm font-semibold text-tinta-1">{t('t5b.automacoes.menuTitulo')}</h2>
          <p className="text-xs text-tinta-3">{t('t5b.automacoes.menuAjuda')}</p>
          {s.pode('conversas.configurar') && !podeEditar && <p className="text-xs text-tinta-3">{t('t5b.automacoes.exigeTodas')}</p>}
          {/* Review r54 robustez (B2): carregando e erro do menu; nada de "Criar menu" (nem formulário vazio) antes da resposta. */}
          <Estado consulta={menus}>
            {() => (
              <div className="grid min-w-0 gap-3 md:grid-cols-2">
                {CANAIS_MENU.map((c) => (
                  <MenuDoCanal key={c} canal={c} menu={menuDe(c)} podeEditar={podeEditar} aoAbrir={() => setCanalAberto(c)} />
                ))}
              </div>
            )}
          </Estado>
        </section>
        <section className="flex min-w-0 flex-col gap-2">
          <h2 className="text-sm font-semibold text-tinta-1">{t('t5b.automacoes.palavrasTitulo')}</h2>
          <p className="text-xs text-tinta-3">{t('t5b.automacoes.palavrasAjuda')}</p>
          <Tabela<PalavraChave> tela="conversas.palavras_chave" caminho="/conversas/palavras-chave" colunas={colunas} aoClicar={(r) => setRegra(r)} vazio={<Vazio texto={t('t5b.automacoes.vazio')} />} />
        </section>
      </div>

      <Drawer
        aberto={!!canalAberto}
        aoFechar={() => setCanalAberto(null)}
        titulo={canalAberto ? `${t('t5b.automacoes.menuTitulo')}: ${t(`w9.canais.${canalAberto}`)}` : ''}
        rodape={
          <>
            {menuAberto && podeEditar && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setRemoverMenu(true)}>
                {t('t5b.automacoes.remover')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setCanalAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            {podeEditar && (
              <Botao variante="primario" type="submit" form="form-menu">
                {tn('comum.salvar')}
              </Botao>
            )}
          </>
        }
      >
        {canalAberto && menus.data && (
          <FormMenu key={`${canalAberto}:${geracao}`} canal={canalAberto} menu={menuAberto} aoConcluir={() => setCanalAberto(null)} aoReabrir={() => setGeracao((g) => g + 1)} />
        )}
      </Drawer>
      <Confirmar
        aberto={removerMenu}
        aoFechar={() => setRemoverMenu(false)}
        aoConfirmar={() => canalAberto && excluirMenu.mutate(canalAberto)}
        carregando={excluirMenu.isPending}
        titulo={t('t5b.automacoes.removerTitulo')}
        texto={t('t5b.automacoes.removerTexto')}
        perigoso
      />

      <Drawer
        aberto={!!regra}
        aoFechar={() => setRegra(null)}
        titulo={regraAtual ? regraAtual.palavras.join(', ') : t('t5b.automacoes.nova')}
        rodape={
          <>
            {regraAtual && podeNoWorkspace(s, 'conversas.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setRemoverRegra(true)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setRegra(null)}>
              {tn('comum.cancelar')}
            </Botao>
            {podeEditar && (
              <Botao variante="primario" type="submit" form="form-palavra-chave">
                {tn('comum.salvar')}
              </Botao>
            )}
          </>
        }
      >
        {regra && (
          <Formulario
            key={regraAtual?.id ?? 'nova'}
            id="form-palavra-chave"
            campos={campos}
            inicial={regraAtual ? { ...regraAtual, canal: regraAtual.canal ?? '', fila_id: regraAtual.fila_id ?? '' } : { palavras: [], resposta: '', canal: '', fila_id: '', ativa: true }}
            aoEnviar={async (v) => {
              const corpo = {
                palavras: (Array.isArray(v.palavras) ? (v.palavras as string[]) : []).map((x) => x.trim()).filter(Boolean),
                resposta: String(v.resposta ?? ''),
                canal: v.canal ? String(v.canal) : null,
                fila_id: v.fila_id ? String(v.fila_id) : null,
                ativa: !!v.ativa,
              };
              if (regraAtual) await patch(`/conversas/palavras-chave/${regraAtual.id}`, corpo);
              else await post('/conversas/palavras-chave', corpo);
              await atualizarRegras();
              toast.sucesso(tn('comum.salvo'));
              setRegra(null);
            }}
          />
        )}
      </Drawer>
      <Confirmar
        aberto={removerRegra}
        aoFechar={() => setRemoverRegra(false)}
        aoConfirmar={() => regraAtual && excluirRegra.mutate(regraAtual.id)}
        carregando={excluirRegra.isPending}
        titulo={t('t5b.automacoes.removerRegraTitulo')}
        texto={t('t5b.automacoes.removerRegraTexto')}
        perigoso
      />
    </>
  );
}
